import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { CheckGroup, DynRows, Field, YesNo } from "../components/fields";
import { AttachmentInput, PublicLinkButton } from "../components/FormLink";
import CountrySelect from "../components/CountrySelect";
import SignaturePad from "../components/SignaturePad";
import FormRecordsList from "../components/FormRecordsList";
import { Plus } from "lucide-react";
import DateInput from "../components/DateInput";

const initial = {
  legal_name: "", trade_name: "", tax_id: "", incorporation_country: "", economic_sector: "",
  products_services: "", address: "", phone: "", fax: "", website: "", email: "",
  registration_date: "", branches: "", employees: "",
  directors: [],
  legal_rep: { full_name: "", position: "", id_number: "", address: "", nationality: "", profession: "" },
  ubo: { full_name: "", id_number: "", address: "", phone: "", email: "", nationality: "", profession: "", ownership_pct: "" },
  rep_pep: { is_pep: null, pep_position: "", pep_date: "", is_relative: null, relative_name: "" },
  payment_methods: [], payment_other: "", funds_origin: "", business_desc: "",
  bank_refs: [], commercial_refs: [], clients: [], suppliers: [],
  dd_process: null, dd_process_desc: "", investigated: null, investigated_desc: "",
  political: null, political_desc: "", shareholders_other: null, shareholders_other_desc: "",
  client_name: "", signer_position: "", date: new Date().toISOString().slice(0, 10), signature: null,
};

export default function FormJuridica({ publicToken = null }) {
  const { t } = useLang();
  const locationEdit = useLocation().state?.edit || null;
  const [editDoc, setEditDoc] = useState(locationEdit);
  const [showForm, setShowForm] = useState(!!locationEdit);
  const [d, setD] = useState(() => (locationEdit ? { ...initial, ...locationEdit.data } : initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [atts, setAtts] = useState([]);
  const [docs, setDocs] = useState({});
  const DOC_TYPES = [
    { key: "aviso_operaciones", label: t("j.doc_aviso") },
    { key: "registro_publico", label: t("j.doc_registro_publico") },
    { key: "cedula_pasaporte", label: t("j.doc_cedula") },
    { key: "referencia_bancaria", label: t("j.doc_ref_bancaria") },
  ];
  const onDocFile = (key) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return setDocs((p) => ({ ...p, [key]: null }));
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setDocs((p) => ({ ...p, [key]: { filename: f.name, file_base64: rd.result } }));
    rd.readAsDataURL(f);
  };
  const [done, setDone] = useState(null);
  const [sec, setSec] = useState(0);

  const set = (k) => (e) => setD({ ...d, [k]: e?.target ? e.target.value : e });
  const setN = (obj, k) => (e) => setD({ ...d, [obj]: { ...d[obj], [k]: e?.target ? e.target.value : e } });
  const inp = (key, testid, type = "text") => (type === "date"
    ? <DateInput value={d[key]} onChange={set(key)} data-testid={testid} />
    : <input className="ec-input" type={type} value={d[key]} onChange={set(key)} data-testid={testid} />);
  const inpN = (obj, key, testid, type = "text") => (type === "date"
    ? <DateInput value={d[obj][key]} onChange={setN(obj, key)} data-testid={testid} />
    : <input className="ec-input" type={type} value={d[obj][key]} onChange={setN(obj, key)} data-testid={testid} />);

  const scrollToForm = () => setTimeout(() => document.querySelector('[data-testid="pj-form-wrap"]')?.scrollIntoView({ behavior: "smooth" }), 80);

  const newForm = () => {
    setEditDoc(null);
    setD(initial);
    setDocs({});
    setSaved(null);
    setSec(0);
    setShowForm(true);
    scrollToForm();
  };

  const startEdit = (doc) => {
    setEditDoc(doc);
    setD({ ...initial, ...doc.data });
    setSaved(null);
    setSec(0);
    setShowForm(true);
    scrollToForm();
  };
  const yn = t("common.yes"), nn = t("common.no");

  const sections = [
    {
      title: t("j.sec1"),
      content: (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label={t("j.legal_name")}>{inp("legal_name", "pj-legal-name")}</Field>
          <Field label={t("j.trade_name")}>{inp("trade_name", "pj-trade-name")}</Field>
          <Field label={t("j.tax_id")}>{inp("tax_id", "pj-tax-id")}</Field>
          <Field label={t("j.incorporation_country")}><CountrySelect value={d.incorporation_country} onChange={set("incorporation_country")} testid="pj-incorporation-country" /></Field>
          <Field label={t("j.economic_sector")}>{inp("economic_sector", "pj-economic-sector")}</Field>
          <Field label={t("j.registration_date")}>{inp("registration_date", "pj-registration-date", "date")}</Field>
          <Field label={t("j.products_services")} className="sm:col-span-2 lg:col-span-3">
            <textarea className="ec-input" rows={2} value={d.products_services} onChange={set("products_services")} data-testid="pj-products-services" />
          </Field>
          <Field label={t("j.address")} className="sm:col-span-2">{inp("address", "pj-address")}</Field>
          <Field label={t("j.phone")}>{inp("phone", "pj-phone")}</Field>
          <Field label={t("j.fax")}>{inp("fax", "pj-fax")}</Field>
          <Field label={t("j.website")}>{inp("website", "pj-website")}</Field>
          <Field label={t("j.email")}>{inp("email", "pj-email", "email")}</Field>
          <Field label={t("j.branches")}>{inp("branches", "pj-branches", "number")}</Field>
          <Field label={t("j.employees")}>{inp("employees", "pj-employees", "number")}</Field>
        </div>
      ),
    },
    {
      title: t("j.sec2"),
      content: (
        <DynRows testid="pj-directors" rows={d.directors} onChange={(v) => setD({ ...d, directors: v })} columns={[
          { key: "full_name", label: t("j.full_name") }, { key: "id_number", label: t("j.id_number") },
          { key: "board_position", label: t("j.board_position") },
          { key: "is_pep", label: t("j.is_pep"), type: "select", options: [nn, yn] },
          { key: "current_position", label: t("j.current_position") }, { key: "current_since", label: t("j.current_since"), type: "date" },
          { key: "previous_position", label: t("j.previous_position") }, { key: "previous_until", label: t("j.previous_until"), type: "date" },
        ]} />
      ),
    },
    {
      title: t("j.sec3"),
      content: (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label={t("j.full_name")}>{inpN("legal_rep", "full_name", "pj-rep-name")}</Field>
          <Field label={t("j.rep_position")}>{inpN("legal_rep", "position", "pj-rep-position")}</Field>
          <Field label={t("j.id_number")}>{inpN("legal_rep", "id_number", "pj-rep-id")}</Field>
          <Field label={t("j.rep_address")}>{inpN("legal_rep", "address", "pj-rep-address")}</Field>
          <Field label={t("j.rep_nationality")}><CountrySelect value={d.legal_rep.nationality} onChange={setN("legal_rep", "nationality")} testid="pj-rep-nationality" /></Field>
          <Field label={t("j.rep_profession")}>{inpN("legal_rep", "profession", "pj-rep-profession")}</Field>
        </div>
      ),
    },
    {
      title: t("j.sec4"),
      content: (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label={t("j.ubo_name")}>{inpN("ubo", "full_name", "pj-ubo-name")}</Field>
          <Field label={t("j.id_number")}>{inpN("ubo", "id_number", "pj-ubo-id")}</Field>
          <Field label={t("j.rep_address")}>{inpN("ubo", "address", "pj-ubo-address")}</Field>
          <Field label={t("j.ubo_phone")}>{inpN("ubo", "phone", "pj-ubo-phone")}</Field>
          <Field label={t("j.ubo_email")}>{inpN("ubo", "email", "pj-ubo-email", "email")}</Field>
          <Field label={t("j.rep_nationality")}><CountrySelect value={d.ubo.nationality} onChange={setN("ubo", "nationality")} testid="pj-ubo-nationality" /></Field>
          <Field label={t("j.rep_profession")}>{inpN("ubo", "profession", "pj-ubo-profession")}</Field>
          <Field label={t("j.ubo_pct")}>{inpN("ubo", "ownership_pct", "pj-ubo-pct", "number")}</Field>
        </div>
      ),
    },
    {
      title: t("j.sec5"),
      content: (
        <div className="space-y-5">
          <Field label={t("j.rep_is_pep")}><YesNo value={d.rep_pep.is_pep} onChange={(v) => setD({ ...d, rep_pep: { ...d.rep_pep, is_pep: v } })} testid="pj-rep-is-pep" /></Field>
          {d.rep_pep.is_pep && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("j.rep_pep_position")}>{inpN("rep_pep", "pep_position", "pj-rep-pep-position")}</Field>
              <Field label={t("j.rep_pep_date")}>{inpN("rep_pep", "pep_date", "pj-rep-pep-date", "date")}</Field>
            </div>
          )}
          <Field label={t("j.rep_is_relative")}><YesNo value={d.rep_pep.is_relative} onChange={(v) => setD({ ...d, rep_pep: { ...d.rep_pep, is_relative: v } })} testid="pj-rep-is-relative" /></Field>
          {d.rep_pep.is_relative && <Field label={t("j.rep_relative_name")}>{inpN("rep_pep", "relative_name", "pj-rep-relative-name")}</Field>}
        </div>
      ),
    },
    {
      title: t("j.sec6"),
      content: (
        <div className="space-y-5">
          <Field label={t("j.payment_methods")}>
            <CheckGroup
              options={[t("n.pm_check"), t("j.pm_credit_debit"), t("n.pm_cc"), t("j.pm_wire"), t("n.pm_ach"), t("n.pm_cash"), t("n.pm_other")]}
              values={d.payment_methods}
              onChange={(v) => setD({ ...d, payment_methods: v })}
              testidPrefix="pj-pm"
            />
          </Field>
          <Field label={t("n.payment_other")}>{inp("payment_other", "pj-payment-other")}</Field>
          <Field label={t("j.funds_origin")}><textarea className="ec-input" rows={3} value={d.funds_origin} onChange={set("funds_origin")} data-testid="pj-funds-origin" /></Field>
          <Field label={t("j.business_desc")}><textarea className="ec-input" rows={3} value={d.business_desc} onChange={set("business_desc")} data-testid="pj-business-desc" /></Field>
        </div>
      ),
    },
    { title: t("j.sec7"), content: <DynRows testid="pj-bank-refs" rows={d.bank_refs} onChange={(v) => setD({ ...d, bank_refs: v })} columns={[{ key: "bank", label: t("n.bank") }, { key: "contact", label: t("n.contact") }, { key: "position", label: t("j.position_col") }, { key: "phone", label: t("n.phone") }]} /> },
    { title: t("j.sec8"), content: <DynRows testid="pj-commercial-refs" rows={d.commercial_refs} onChange={(v) => setD({ ...d, commercial_refs: v })} columns={[{ key: "name", label: t("n.legal_name_ref") }, { key: "contact", label: t("n.contact") }, { key: "position", label: t("j.position_col") }, { key: "phone", label: t("n.phone") }]} /> },
    { title: t("j.sec9"), content: <DynRows testid="pj-clients" rows={d.clients} onChange={(v) => setD({ ...d, clients: v })} columns={[{ key: "name", label: t("j.full_name") }, { key: "address", label: t("j.address") }, { key: "phone", label: t("n.phone") }]} /> },
    { title: t("j.sec10"), content: <DynRows testid="pj-suppliers" rows={d.suppliers} onChange={(v) => setD({ ...d, suppliers: v })} columns={[{ key: "name", label: t("j.full_name") }, { key: "address", label: t("j.address") }, { key: "phone", label: t("n.phone") }]} /> },
    {
      title: t("j.sec11"),
      content: (
        <div className="space-y-5">
          <Field label={t("n.dd_process")}><YesNo value={d.dd_process} onChange={(v) => setD({ ...d, dd_process: v })} testid="pj-dd-process" /></Field>
          {d.dd_process && <Field label={t("n.dd_process_desc")}><textarea className="ec-input" rows={2} value={d.dd_process_desc} onChange={set("dd_process_desc")} data-testid="pj-dd-process-desc" /></Field>}
          <Field label={t("n.investigated")}><YesNo value={d.investigated} onChange={(v) => setD({ ...d, investigated: v })} testid="pj-investigated" /></Field>
          {d.investigated && <Field label={t("n.investigated_desc")}><textarea className="ec-input" rows={2} value={d.investigated_desc} onChange={set("investigated_desc")} data-testid="pj-investigated-desc" /></Field>}
          <Field label={t("j.political")}><YesNo value={d.political} onChange={(v) => setD({ ...d, political: v })} testid="pj-political" /></Field>
          {d.political && <Field label={t("j.political_desc")}><textarea className="ec-input" rows={2} value={d.political_desc} onChange={set("political_desc")} data-testid="pj-political-desc" /></Field>}
          <Field label={t("j.shareholders_other")}><YesNo value={d.shareholders_other} onChange={(v) => setD({ ...d, shareholders_other: v })} testid="pj-shareholders-other" /></Field>
          {d.shareholders_other && <Field label={t("j.shareholders_other_desc")}><textarea className="ec-input" rows={2} value={d.shareholders_other_desc} onChange={set("shareholders_other_desc")} data-testid="pj-shareholders-other-desc" /></Field>}
        </div>
      ),
    },
    {
      title: t("j.sec12"),
      content: (
        <div className="space-y-5">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4">{t("j.decl_text")}</p>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-4">
              <Field label={t("j.rep_name_sign")}>{inp("client_name", "pj-signer-name")}</Field>
              <Field label={t("j.signer_position")}>{inp("signer_position", "pj-signer-position")}</Field>
              <Field label={t("common.date")}>{inp("date", "pj-sign-date", "date")}</Field>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="pj-docs">
                {DOC_TYPES.map((dt) => (
                  <Field key={dt.key} label={dt.label}>
                    <input className="ec-input" type="file" onChange={onDocFile(dt.key)} data-testid={`pj-doc-${dt.key}`} />
                    {docs[dt.key] && <div className="text-xs font-semibold text-emerald-700 mt-1">{docs[dt.key].filename}</div>}
                  </Field>
                ))}
              </div>
              <AttachmentInput value={atts} onChange={setAtts} testid="pj-attachments" />
            </div>
            <Field label={t("forms.signature")}><SignaturePad onChange={(v) => setD((p) => ({ ...p, signature: v }))} /></Field>
          </div>
        </div>
      ),
    },
  ];

  const submit = async () => {
    if (!d.signature) {
      toast.error(t("forms.sign_required"));
      setSec(sections.length - 1);
      return;
    }
    setBusy(true);
    const typedAtts = DOC_TYPES.filter((dt) => docs[dt.key]).map((dt) => ({ ...docs[dt.key], note: dt.label }));
    const allAtts = [...typedAtts, ...atts];
    try {
      if (publicToken) {
        const { data } = await api.post(`/public/form/${publicToken}`, { data: d, attachments: allAtts });
        setDone(data.form_number);
      } else if (editDoc) {
        await api.patch(`/forms/${editDoc.id}`, { data: d });
        setSaved({ id: editDoc.id, form_number: editDoc.form_number });
        toast.success(t("forms.updated"));
      } else {
        const { data } = await api.post("/forms", { form_type: "dd_persona_juridica", data: d, attachments: allAtts });
        setSaved(data);
        toast.success(`${t("forms.save_success")} — ${data.form_number}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="form-public-done">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="form-juridica-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("j.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("j.subtitle")}</p>
        </div>
        {!publicToken && (
          <div className="flex items-center gap-2">
            <button onClick={newForm} data-testid="pj-create-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
              <Plus className="w-4 h-4" /> {t("frl.new")}
            </button>
            <PublicLinkButton formType="dd_persona_juridica" testid="pj-public-link" />
          </div>
        )}
      </div>

      {!publicToken && <FormRecordsList formType="dd_persona_juridica" onEdit={startEdit} refresh={saved} testid="pj" />}

      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="form-saved-banner">
          <span className="text-sm text-emerald-800 font-semibold">{t("forms.doc_no")} <span className="font-mono">{saved.form_number}</span></span>
          <button onClick={() => downloadFile(`/forms/${saved.id}/pdf`, `${saved.form_number}.pdf`)} data-testid="download-form-pdf" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
            <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
          </button>
        </div>
      )}

      {(publicToken || showForm) && (
        <div className="space-y-6" data-testid="pj-form-wrap">
      <div className="space-y-1.5" data-testid="pj-progress-wrap">
        <div className="flex justify-between text-[11px] font-semibold text-[#8A94A2]">
          <span>{sections[sec].title}</span>
          <span data-testid="pj-progress-pct">{Math.round(((sec + 1) / sections.length) * 100)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#EFF4FC] overflow-hidden">
          <div className="h-full rounded-full bg-[#044CBC] transition-all duration-500" style={{ width: `${((sec + 1) / sections.length) * 100}%` }} data-testid="pj-progress" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {sections.map((s, i) => (
          <button key={i} onClick={() => setSec(i)} data-testid={`pj-tab-${i}`} className={`shrink-0 px-3.5 py-2 rounded-lg text-xs font-bold transition-colors ${sec === i ? "bg-[#16335F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            {s.title.split(".")[0]}
          </button>
        ))}
      </div>

      <div className="ec-card p-6">
        <h2 className="font-heading font-bold text-slate-900 mb-5">{sections[sec].title}</h2>
        {sections[sec].content}
        <div className="flex justify-between mt-8">
          <button onClick={() => setSec(Math.max(0, sec - 1))} disabled={sec === 0} data-testid="pj-prev" className="px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-30">←</button>
          {sec < sections.length - 1 ? (
            <button onClick={() => setSec(sec + 1)} data-testid="pj-next" className="px-6 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C]">→</button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="pj-submit" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {busy ? t("common.saving") : t("common.save")}
            </button>
          )}
        </div>
      </div>
        </div>
      )}
    </div>
  );
}
